import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Evidence } from '../types';

/**
 * Words of estimative probability (after Sherman Kent / ICD 203), so AI findings are
 * read with the right weight rather than as bare percentages.
 */
export function estimativeLabel(confidence: number): string {
  if (confidence >= 90) return 'Almost certain';
  if (confidence >= 75) return 'Very likely';
  if (confidence >= 60) return 'Likely';
  if (confidence >= 40) return 'Roughly even';
  if (confidence >= 20) return 'Unlikely';
  return 'Remote';
}

const tone = (confidence: number) =>
  confidence >= 75
    ? 'bg-emerald-50 text-emerald-700'
    : confidence >= 50
      ? 'bg-sky-50 text-sky-700'
      : confidence >= 30
        ? 'bg-amber-50 text-amber-700'
        : 'bg-neutral-100 text-neutral-600';

export const ConfidenceBadge: React.FC<{ value: number; className?: string; showValue?: boolean }> = ({
  value,
  className = '',
  showValue = true,
}) => (
  <span
    title={`${Math.round(value)}% confidence`}
    className={`inline-flex h-5 items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[11px] font-medium leading-none ${tone(value)} ${className}`}
  >
    <span className="flex items-end gap-[2px]" aria-hidden="true">
      {[20, 45, 70, 90].map((threshold, i) => (
        <span
          key={threshold}
          className={`w-[3px] rounded-[1px] ${value >= threshold ? 'bg-current' : 'bg-current opacity-25'}`}
          style={{ height: 4 + i * 2 }}
        />
      ))}
    </span>
    {estimativeLabel(value)}
    {showValue && <span className="tabular-nums opacity-60">{Math.round(value)}%</span>}
  </span>
);

const hostOf = (url?: string | null) => {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

/** Claims with their sources, numbered like footnotes */
export const EvidenceList: React.FC<{ evidence: Evidence[]; className?: string }> = ({ evidence, className = '' }) => {
  if (!evidence.length) {
    return <p className={`text-[12px] text-neutral-400 ${className}`}>No sources cited</p>;
  }
  return (
    <ol className={`space-y-2 ${className}`}>
      {evidence.map((item, i) => {
        const host = hostOf(item.url);
        return (
          <li key={i} className="flex gap-2 text-[12.5px] leading-snug">
            <span className="mt-px inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded bg-neutral-100 text-[10px] tabular-nums text-neutral-500">
              {i + 1}
            </span>
            <span className="min-w-0">
              <span className="text-neutral-700">{item.claim}</span>
              {(item.source || host || item.date) && (
                <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11.5px] text-neutral-400">
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-w-0 items-center gap-1 truncate text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
                    >
                      {item.source || host}
                      <ExternalLink className="h-3 w-3 flex-shrink-0" strokeWidth={1.75} />
                    </a>
                  ) : (
                    item.source && <span>{item.source}</span>
                  )}
                  {item.date && <span>· {item.date}</span>}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
};
